import type { PermissionGroup, RoleRecord } from '@/types/portal';

/**
 * The full resource.action permission catalog, grouped the same way the roles
 * matrix renders it. Risk levels drive the highlight on sensitive grants.
 */
export const permissionCatalog: PermissionGroup[] = [
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: 'ri-megaphone-line',
    permissions: [
      { key: 'campaigns.view', label: 'View campaigns', description: 'See campaign briefs, rosters and progress', risk: 'Standard' },
      { key: 'campaigns.manage', label: 'Manage campaigns', description: 'Create, edit and run campaigns end to end', risk: 'Sensitive' },
      { key: 'campaigns.brief', label: 'Author briefs', description: 'Write and publish campaign briefs', risk: 'Standard' },
      { key: 'campaigns.approve_content', label: 'Approve content', description: 'Sign off creator deliverables', risk: 'Sensitive' },
      { key: 'campaigns.close', label: 'Close campaigns', description: 'Archive and lock a completed campaign', risk: 'Sensitive' },
    ],
  },
  {
    id: 'discovery',
    label: 'Creator Discovery',
    icon: 'ri-search-eye-line',
    permissions: [
      { key: 'discovery.view', label: 'Use discovery', description: 'Search and browse the creator pool', risk: 'Standard' },
      { key: 'discovery.manage', label: 'Manage lists', description: 'Save, edit and share creator shortlists', risk: 'Standard' },
      { key: 'discovery.export', label: 'Export creators', description: 'Download creator data and shortlists', risk: 'Sensitive' },
    ],
  },
  {
    id: 'creators',
    label: 'Creators',
    icon: 'ri-user-star-line',
    permissions: [
      { key: 'creators.view', label: 'View creators', description: 'Open creator profiles and vetting detail', risk: 'Standard' },
      { key: 'creators.invite', label: 'Invite creators', description: 'Send campaign invitations to creators', risk: 'Standard' },
      { key: 'creators.contract', label: 'Contract creators', description: 'Issue and finalise creator contracts', risk: 'Sensitive' },
    ],
  },
  {
    id: 'invoices',
    label: 'Invoices',
    icon: 'ri-file-list-3-line',
    permissions: [
      { key: 'invoices.view', label: 'View invoices', description: 'See creator invoices and status', risk: 'Standard' },
      { key: 'invoices.manage', label: 'Manage invoices', description: 'Edit, hold and process invoices', risk: 'Sensitive' },
      { key: 'invoices.approve', label: 'Approve invoices', description: 'Approve invoices for payment', risk: 'Critical' },
    ],
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: 'ri-send-plane-line',
    permissions: [
      { key: 'payments.view', label: 'View payments', description: 'See batches, single payments and the ledger', risk: 'Standard' },
      { key: 'payments.manage', label: 'Build payments', description: 'Prepare payment batches and single payments', risk: 'Sensitive' },
      { key: 'payments.approve', label: 'Approve payments', description: 'Release prepared payments to the rails', risk: 'Critical' },
    ],
  },
  {
    id: 'payouts',
    label: 'Payouts',
    icon: 'ri-bank-card-line',
    permissions: [
      { key: 'payouts.view', label: 'View payouts', description: 'See scheduled and completed disbursements', risk: 'Standard' },
      { key: 'payouts.manage', label: 'Manage payouts', description: 'Retry, hold and release disbursements', risk: 'Critical' },
      { key: 'payouts.approve', label: 'Approve payouts', description: 'Approve payouts above the ceiling', risk: 'Critical' },
    ],
  },
  {
    id: 'wallets',
    label: 'Wallets',
    icon: 'ri-wallet-3-line',
    permissions: [
      { key: 'wallets.view', label: 'View wallets', description: 'See funding and creator wallet balances', risk: 'Standard' },
      { key: 'wallets.manage', label: 'Manage wallets', description: 'Edit wallet settings and freeze wallets', risk: 'Sensitive' },
      { key: 'wallets.fund', label: 'Fund wallets', description: 'Top up funding wallets from a bank account', risk: 'Critical' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'ri-bar-chart-box-line',
    permissions: [
      { key: 'reports.view', label: 'View reports', description: 'Open campaign, spend and creator reports', risk: 'Standard' },
      { key: 'reports.export', label: 'Export reports', description: 'Download reports as CSV or PDF', risk: 'Sensitive' },
    ],
  },
  {
    id: 'staff',
    label: 'Users',
    icon: 'ri-team-line',
    permissions: [
      { key: 'staff.view', label: 'View users', description: 'See portal staff and their roles', risk: 'Standard' },
      { key: 'staff.manage', label: 'Manage users', description: 'Invite, edit and deactivate staff', risk: 'Sensitive' },
    ],
  },
  {
    id: 'roles',
    label: 'Roles & Permissions',
    icon: 'ri-shield-user-line',
    permissions: [
      { key: 'roles.view', label: 'View roles', description: 'See roles and their permission grants', risk: 'Standard' },
      { key: 'roles.manage', label: 'Manage roles', description: 'Create roles and change permissions', risk: 'Critical' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'ri-settings-3-line',
    permissions: [
      { key: 'settings.view', label: 'View settings', description: 'Open instance configuration', risk: 'Standard' },
      { key: 'settings.manage', label: 'Manage settings', description: 'Change workspace and branding config', risk: 'Critical' },
      { key: 'settings.branding', label: 'Manage branding', description: 'Edit agency profile, logo and colours', risk: 'Sensitive' },
    ],
  },
  {
    id: 'audit',
    label: 'Audit Log',
    icon: 'ri-history-line',
    permissions: [
      { key: 'audit.view', label: 'View audit log', description: 'See every consequential action and actor', risk: 'Sensitive' },
    ],
  },
];

/**
 * The roles an agency can assign. System roles ship with the instance and cannot
 * be deleted; custom roles are cloned from one and edited.
 */
export const roleRecords: RoleRecord[] = [
  {
    id: 'admin',
    name: 'Agency Administrator',
    description: 'Owns the licensed instance, branding, wallets and roles.',
    isSystem: true,
    memberCount: 1,
    approvalsCeiling: 'Unlimited',
    permissions: [
      'campaigns.view', 'campaigns.manage', 'campaigns.brief', 'campaigns.approve_content', 'campaigns.close',
      'discovery.view', 'discovery.manage', 'discovery.export',
      'creators.view', 'creators.invite', 'creators.contract',
      'invoices.view', 'invoices.manage', 'invoices.approve',
      'payments.view', 'payments.manage', 'payments.approve',
      'payouts.view', 'payouts.manage', 'payouts.approve',
      'wallets.view', 'wallets.manage', 'wallets.fund',
      'reports.view', 'reports.export',
      'staff.view', 'staff.manage',
      'roles.view', 'roles.manage',
      'settings.view', 'settings.manage', 'settings.branding',
      'audit.view',
    ],
  },
  {
    id: 'finance',
    name: 'Finance User',
    description: 'Approves invoices, funds wallets, executes payouts and reconciles.',
    isSystem: true,
    memberCount: 3,
    approvalsCeiling: 'KES 2,000,000',
    permissions: [
      'campaigns.view',
      'invoices.view', 'invoices.manage', 'invoices.approve',
      'payments.view', 'payments.manage', 'payments.approve',
      'payouts.view', 'payouts.manage', 'payouts.approve',
      'wallets.view', 'wallets.manage', 'wallets.fund',
      'reports.view', 'reports.export',
      'audit.view',
    ],
  },
  {
    id: 'creator_manager',
    name: 'Creator Manager',
    description: 'Runs campaigns end to end: discovery, invites, contracts, deliverables.',
    isSystem: true,
    memberCount: 6,
    approvalsCeiling: 'KES 250,000',
    permissions: [
      'campaigns.view', 'campaigns.manage', 'campaigns.brief', 'campaigns.approve_content',
      'discovery.view', 'discovery.manage', 'discovery.export',
      'creators.view', 'creators.invite', 'creators.contract',
      'invoices.view',
      'reports.view',
    ],
  },
  {
    id: 'viewer',
    name: 'Campaign Viewer',
    description: 'Reads campaign progress and performance. Takes no actions.',
    isSystem: true,
    memberCount: 4,
    approvalsCeiling: 'None',
    permissions: ['campaigns.view', 'reports.view'],
  },
  {
    id: 'client',
    name: 'Client User',
    description: 'An agency client with read access scoped to their campaign.',
    isSystem: true,
    memberCount: 2,
    approvalsCeiling: 'None',
    permissions: ['campaigns.view', 'reports.view', 'reports.export'],
  },
];

export const approvalCeilings = [
  'None',
  'KES 100,000',
  'KES 250,000',
  'KES 500,000',
  'KES 2,000,000',
  'Unlimited',
];